import {
  FeedbackAnswer,
  FeedbackBrowserData,
} from '../../../types/instant-lease-api';

/** A selectable single-choice option. */
export interface FeedbackOption {
  value: string;
  label: string;
}

/** Membership check that accepts any string against a literal-union list. */
export const isOneOf = (list: readonly string[], value: string): boolean =>
  list.includes(value);

/** Stable question keys used in the submitted payload. */
export const FeedbackQuestion = {
  EaseOfUse: 'ease_of_use',
  UnclearMoment: 'unclear_moment',
  AgeGroup: 'age_group',
  Recommendation: 'recommendation',
} as const satisfies Record<string, string>;

export type FeedbackQuestion =
  (typeof FeedbackQuestion)[keyof typeof FeedbackQuestion];

/** react-hook-form model backing the whole multi-step survey. */
export interface FeedbackFormValues {
  easeOfUse: string;
  easeOfUseComment: string;
  unclearMoment: string;
  unclearCategories: string[];
  unclearComment: string;
  ageGroup: string;
  recommendation: string;
  recommendationComment: string;
}

export const feedbackDefaultValues: FeedbackFormValues = {
  easeOfUse: '',
  easeOfUseComment: '',
  unclearMoment: '',
  unclearCategories: [],
  unclearComment: '',
  ageGroup: '',
  recommendation: '',
  recommendationComment: '',
};

// ---------------------------------------------------------------------------
// Static content (hardcoded — no API fetch, no JSON files)
// ---------------------------------------------------------------------------

export const feedbackText = {
  title: 'Deine Meinung zählt',
  next: 'nächste Seite',
  submit: 'Feedback senden',
  thankYou: {
    headline: 'Vielen Dank für deine Zeit',
    description:
      'Wir freuen uns über dein Feedback. Deine Erfahrungen helfen uns dabei, unseren Service und die Antragsstrecke weiter zu verbessern.',
    close: 'Seite schließen',
  },
};

export const feedbackErrors = {
  selectOption: 'Bitte wähle eine Option aus.',
  easeComment: 'Bitte beschreibe kurz, was dir Probleme bereitet hat.',
  unclearComment: 'Bitte beschreibe kurz, was unklar oder schwierig war.',
  ageGroup: 'Bitte wähle eine Altersklasse aus.',
};

// Question 1 — Wie einfach war der Antrag für dich?
export const easeOfUseQuestion = {
  title: 'Wie einfach war der Antrag für dich?',
};
export const easeOfUseOptions = [
  { value: 'very_easy', label: 'Sehr einfach' },
  { value: 'rather_easy', label: 'Eher einfach' },
  { value: 'partly_difficult', label: 'Teilweise schwierig' },
  { value: 'rather_difficult', label: 'Eher schwierig' },
  { value: 'very_difficult', label: 'Sehr schwierig' },
] as const satisfies readonly FeedbackOption[];
/** Ease options that require a mandatory explanatory comment. */
export const easeOfUseCommentValues = [
  'partly_difficult',
  'rather_difficult',
  'very_difficult',
] as const satisfies readonly string[];
export const easeOfUseCommentPlaceholder =
  'Beschreibe kurz, was dir Probleme bereitet hat';

// Question 2 — Gab es einen unklaren oder schwierigen Moment?
export const unclearMomentQuestion = {
  title: 'Gab es einen unklaren oder schwierigen Moment?',
  categoryTitle: 'In welchem Bereich hattest du Probleme?',
  commentPlaceholder: 'Beschreibe kurz, was unklar oder schwierig war.',
};
export const unclearMomentOptions = [
  { value: 'no', label: 'Nein' },
  { value: 'partly', label: 'Teilweise' },
  { value: 'yes', label: 'Ja' },
] as const satisfies readonly FeedbackOption[];
/** Answers that reveal the category selector + mandatory comment. */
export const unclearMomentDetailValues = [
  'partly',
  'yes',
] as const satisfies readonly string[];
export const unclearCategoryOptions = [
  { value: 'vehicle_selection', label: 'Fahrzeugauswahl' },
  { value: 'registration_login', label: 'Registrierung / Login' },
  { value: 'dealer_selection', label: 'Händlerauswahl' },
  { value: 'credit_check', label: 'Bonitätsprüfung' },
  { value: 'legitimation', label: 'Legitimation' },
  { value: 'contract_documents', label: 'Vertragsunterlagen' },
  { value: 'other', label: 'Sonstiges' },
] as const satisfies readonly FeedbackOption[];

// Question 3 — Zu welcher Altersklasse gehörst du?
export const ageGroupQuestion = {
  title: 'Zu welcher Altersklasse gehörst du?',
};
export const ageGroupOptions = [
  { value: '18_29', label: '18 - 29' },
  { value: '30_45', label: '30 - 45' },
  { value: '46_55', label: '46 - 55' },
  { value: '56_70', label: '56 - 70' },
  { value: 'over_70', label: 'über 70' },
  { value: 'no_answer', label: 'Keine Angaben' },
] as const satisfies readonly FeedbackOption[];

// Question 4 — Würdest du uns deiner Familie und Freunden weiterempfehlen?
export const recommendationQuestion = {
  title: 'Würdest du uns deiner Familie und Freunden weiterempfehlen? (Optional)',
  positivePlaceholder: 'Was hat dir besonders gut gefallen (Optional)?',
  negativePlaceholder: 'Wie können wir uns verbessern? (Optional)',
};
export const recommendationOptions = [
  { value: 'definitely', label: 'Auf jeden Fall' },
  { value: 'probably', label: 'Wahrscheinlich' },
  { value: 'rather_not', label: 'Eher nicht' },
  { value: 'unlikely', label: 'Unwahrscheinlich' },
  { value: 'no', label: 'Nein' },
] as const satisfies readonly FeedbackOption[];
/** Recommendation answers considered positive (drive the follow-up copy). */
export const recommendationPositiveValues = [
  'definitely',
  'probably',
] as const satisfies readonly string[];

// ---------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------

/**
 * Maps the flat form model onto the structured `answer_list` payload.
 * Only answered questions are included; comments/categories are attached
 * to their question and empty values are omitted.
 */
export const buildAnswerList = (
  values: FeedbackFormValues,
): FeedbackAnswer[] => {
  const answers: FeedbackAnswer[] = [];

  if (values.easeOfUse) {
    const comment = values.easeOfUseComment.trim();
    const requiresComment = isOneOf(easeOfUseCommentValues, values.easeOfUse);
    answers.push({
      question: FeedbackQuestion.EaseOfUse,
      answer: values.easeOfUse,
      ...(requiresComment && comment ? { comment } : {}),
    });
  }

  if (values.unclearMoment) {
    const comment = values.unclearComment.trim();
    const showsDetails = isOneOf(
      unclearMomentDetailValues,
      values.unclearMoment,
    );
    answers.push({
      question: FeedbackQuestion.UnclearMoment,
      answer: values.unclearMoment,
      ...(showsDetails && comment ? { comment } : {}),
      ...(showsDetails && values.unclearCategories.length
        ? { categories: values.unclearCategories }
        : {}),
    });
  }

  if (values.ageGroup) {
    answers.push({
      question: FeedbackQuestion.AgeGroup,
      answer: values.ageGroup,
    });
  }

  if (values.recommendation) {
    const comment = values.recommendationComment.trim();
    answers.push({
      question: FeedbackQuestion.Recommendation,
      answer: values.recommendation,
      ...(comment ? { comment } : {}),
    });
  }

  return answers;
};

// ---------------------------------------------------------------------------
// Browser environment
// ---------------------------------------------------------------------------

/** Minimal UA-CH typing — navigator.userAgentData is Chromium-only and not in lib.dom. */
interface UADataLike {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (
    hints: string[],
  ) => Promise<{ platformVersion?: string }>;
}

const uaFallbackMobile = (): boolean => {
  const ua = navigator.userAgent || '';
  // iPadOS 13+ masquerades as macOS but is the only "Mac" with multi-touch
  const iPadOs =
    /Mac/i.test(navigator.platform || '') && navigator.maxTouchPoints > 1;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || iPadOs;
};

const uaFallbackPlatform = (): string => {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Mac/i.test(ua)) return navigator.maxTouchPoints > 1 ? 'iOS' : 'macOS';
  if (/CrOS/i.test(ua)) return 'Chrome OS';
  if (/Linux/i.test(ua)) return 'Linux';
  return navigator.platform || 'unknown';
};

/**
 * Collects OS/browser environment data. Never throws: uses UA-CH
 * (Chromium) when available and degrades gracefully on Firefox/Safari.
 */
export const collectBrowserData = async (): Promise<FeedbackBrowserData> => {
  const data: FeedbackBrowserData = {
    brands: [],
    mobile: false,
    platform: '',
    platformVersion: '',
    resolvedLocale: '',
    timeZone: '',
  };

  try {
    const uaData = (navigator as { userAgentData?: UADataLike }).userAgentData;
    if (uaData) {
      data.brands = (uaData.brands ?? []).map(({ brand, version }) => ({
        brand,
        version,
      }));
      data.mobile = uaData.mobile ?? false;
      data.platform = uaData.platform ?? '';
    } else {
      data.mobile = uaFallbackMobile();
      data.platform = uaFallbackPlatform();
    }
    // High-entropy hint may prompt/reject independently of the low-entropy values
    if (uaData?.getHighEntropyValues) {
      data.platformVersion =
        (await uaData.getHighEntropyValues(['platformVersion']))
          ?.platformVersion ?? '';
    }
  } catch {
    // keep defaults
  }

  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    data.resolvedLocale = resolved.locale ?? '';
    data.timeZone = resolved.timeZone ?? '';
  } catch {
    // Intl unavailable — keep defaults
  }

  return data;
};
